import AsyncStorage from '@react-native-async-storage/async-storage';
import { UserData, OrgUserData, OrgUserDataKey, OrgUserDataValue, defaultUserData, defaultOrgUserData, LastViewedVideo, getOrgUserData } from './user-data-interfaces';

const USER_DATA_STORAGE_KEY = '@octahub/user_data';
const LAST_SELECTED_ORG_KEY = '@octahub/last_selected_org';

export interface UserDataService {
    // Core CRUD operations
    getUserData: () => Promise<UserData>;
    setUserData: (data: UserData) => Promise<UserData>;
    getOrgUserData: (orgId: string) => Promise<OrgUserData>;
    updateOrgUserData: <K extends OrgUserDataKey>(orgId: string, key: K, value: OrgUserDataValue<K>) => Promise<OrgUserData>;
    resetUserData: () => Promise<UserData>;
    resetOrgUserData: (orgId: string) => Promise<OrgUserData>;

    // Last selected organization
    getLastSelectedOrgId: () => Promise<string | null>;
    setLastSelectedOrgId: (orgId: string) => Promise<void>;
    clearLastSelectedOrgId: () => Promise<void>;

    // Bookmark operations
    getBookmarks: (orgId: string) => Promise<string[]>;
    addBookmark: (orgId: string, videoId: string) => Promise<string[]>;
    removeBookmark: (orgId: string, videoId: string) => Promise<string[]>;
    isBookmarked: (videoId: string, bookmarks?: string[]) => boolean;

    // Last viewed operations
    getLastViewed: (orgId: string) => Promise<LastViewedVideo[]>;
    addLastViewed: (orgId: string, videoId: string) => Promise<LastViewedVideo[]>;
    removeLastViewed: (orgId: string, videoId: string) => Promise<LastViewedVideo[]>;
    clearLastViewed: (orgId: string) => Promise<LastViewedVideo[]>;

    // Preferences operations
    getPreferences: (orgId: string) => Promise<OrgUserData['preferences']>;
    updatePreferences: (orgId: string, preferences: Partial<OrgUserData['preferences']>) => Promise<OrgUserData['preferences']>;
}

/**
 * Local storage implementation of user data service
 */
export const createUserDataService = (): UserDataService => {
    const getUserData = async (): Promise<UserData> => {
        try {
            const stored = await AsyncStorage.getItem(USER_DATA_STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored) as UserData;
                return parsed;
            }
            return defaultUserData;
        } catch (error) {
            console.error('Failed to load user data:', error);
            return defaultUserData;
        }
    };

    const setUserData = async (data: UserData): Promise<UserData> => {
        try {
            await AsyncStorage.setItem(USER_DATA_STORAGE_KEY, JSON.stringify(data));
            return data;
        } catch (error) {
            console.error('Failed to save user data:', error);
            throw new Error('Failed to save user data');
        }
    };

    const getOrgUserDataFn = async (orgId: string): Promise<OrgUserData> => {
        const userData = await getUserData();
        return getOrgUserData(userData, orgId);
    };

    const updateOrgUserData = async <K extends OrgUserDataKey>(
        orgId: string,
        key: K,
        value: OrgUserDataValue<K>
    ): Promise<OrgUserData> => {
        const userData = await getUserData();
        const currentOrgData = getOrgUserData(userData, orgId);
        const updatedOrgData = { ...currentOrgData, [key]: value };
        const updatedUserData = { ...userData, [orgId]: updatedOrgData };
        await setUserData(updatedUserData);
        return updatedOrgData;
    };

    const resetUserData = async (): Promise<UserData> => {
        await clearLastSelectedOrgId();
        return setUserData(defaultUserData);
    };

    const resetOrgUserData = async (orgId: string): Promise<OrgUserData> => {
        const userData = await getUserData();
        const updatedUserData = { ...userData, [orgId]: defaultOrgUserData };
        await setUserData(updatedUserData);
        return defaultOrgUserData;
    };

    // Bookmark operations
    const getBookmarks = async (orgId: string): Promise<string[]> => {
        const orgData = await getOrgUserDataFn(orgId);
        return orgData.bookmarks;
    };

    const addBookmark = async (orgId: string, videoId: string): Promise<string[]> => {
        const orgData = await getOrgUserDataFn(orgId);
        if (!orgData.bookmarks.includes(videoId)) {
            const updatedBookmarks = [...orgData.bookmarks, videoId];
            await updateOrgUserData(orgId, 'bookmarks', updatedBookmarks);
            return updatedBookmarks;
        }
        return orgData.bookmarks;
    };

    const removeBookmark = async (orgId: string, videoId: string): Promise<string[]> => {
        const orgData = await getOrgUserDataFn(orgId);
        const updatedBookmarks = orgData.bookmarks.filter(id => id !== videoId);
        await updateOrgUserData(orgId, 'bookmarks', updatedBookmarks);
        return updatedBookmarks;
    };

    const isBookmarked = (videoId: string, bookmarks?: string[]): boolean => {
        if (bookmarks) {
            return bookmarks.includes(videoId);
        }
        // If no bookmarks array provided, this is a sync check and should be handled by hooks
        return false;
    };

    // Last viewed operations
    const getLastViewed = async (orgId: string): Promise<LastViewedVideo[]> => {
        const orgData = await getOrgUserDataFn(orgId);
        return orgData.lastViewed;
    };

    const addLastViewed = async (orgId: string, videoId: string): Promise<LastViewedVideo[]> => {
        const orgData = await getOrgUserDataFn(orgId);
        const viewedAt = new Date().toISOString();
        const newVideo: LastViewedVideo = { videoId, viewedAt };

        // Remove existing entry for this video if it exists
        const filteredViewed = orgData.lastViewed.filter(v => v.videoId !== newVideo.videoId);

        // Add new entry at the beginning (most recent first)
        const updatedViewed = [newVideo, ...filteredViewed];

        // Keep only last 50 videos
        const limitedViewed = updatedViewed.slice(0, 50);

        await updateOrgUserData(orgId, 'lastViewed', limitedViewed);
        return limitedViewed;
    };

    const removeLastViewed = async (orgId: string, videoId: string): Promise<LastViewedVideo[]> => {
        const orgData = await getOrgUserDataFn(orgId);
        const updatedViewed = orgData.lastViewed.filter(v => v.videoId !== videoId);
        await updateOrgUserData(orgId, 'lastViewed', updatedViewed);
        return updatedViewed;
    };

    const clearLastViewed = async (orgId: string): Promise<LastViewedVideo[]> => {
        await updateOrgUserData(orgId, 'lastViewed', []);
        return [];
    };

    // Preferences operations
    const getPreferences = async (orgId: string): Promise<OrgUserData['preferences']> => {
        const orgData = await getOrgUserDataFn(orgId);
        return orgData.preferences;
    };

    const updatePreferences = async (orgId: string, preferences: Partial<OrgUserData['preferences']>): Promise<OrgUserData['preferences']> => {
        const orgData = await getOrgUserDataFn(orgId);
        const updatedPreferences = { ...orgData.preferences, ...preferences };
        await updateOrgUserData(orgId, 'preferences', updatedPreferences);
        return updatedPreferences;
    };

    // Last selected organization operations
    const getLastSelectedOrgId = async (): Promise<string | null> => {
        try {
            return await AsyncStorage.getItem(LAST_SELECTED_ORG_KEY);
        } catch (error) {
            console.error('Failed to get last selected org:', error);
            return null;
        }
    };

    const setLastSelectedOrgId = async (orgId: string): Promise<void> => {
        try {
            await AsyncStorage.setItem(LAST_SELECTED_ORG_KEY, orgId);
        } catch (error) {
            console.error('Failed to set last selected org:', error);
        }
    };

    const clearLastSelectedOrgId = async (): Promise<void> => {
        try {
            await AsyncStorage.removeItem(LAST_SELECTED_ORG_KEY);
        } catch (error) {
            console.error('Failed to clear last selected org:', error);
        }
    };

    return {
        getUserData,
        setUserData,
        getOrgUserData: getOrgUserDataFn,
        updateOrgUserData,
        resetUserData,
        resetOrgUserData,
        getBookmarks,
        addBookmark,
        removeBookmark,
        isBookmarked,
        getLastViewed,
        addLastViewed,
        removeLastViewed,
        clearLastViewed,
        getPreferences,
        updatePreferences,
        getLastSelectedOrgId,
        setLastSelectedOrgId,
        clearLastSelectedOrgId,
    };
};

// Export singleton instance
export const userDataService = createUserDataService();
